import { useCallback, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { useLocation, useNavigate } from "react-router-dom";

import apiClient from "../utils/apiClient";
import { intendedPathAtom, maintenanceStatusAtom } from "../atom/maintenance";

const STATUS_URL = "/site-status";
const REFRESH_INTERVAL = 2 * 60 * 1000;

const normalizeStatus = (data = {}) => ({
  enabled: data.enabled === 1 || data.enabled === true,
  manualLock: data.manual_lock === 1 || data.manual_lock === true,
  reason: data.reason || "",
  message: data.message || "Site under maintenance, please come back later!",
  updatedAt: data.updated_at || null,
});

const useMaintenanceStatus = ({ enableAuto = true } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useAtom(maintenanceStatusAtom);
  const [intendedPath, setIntendedPath] = useAtom(intendedPathAtom);
  const intervalRef = useRef(null);

  const checkStatus = useCallback(async () => {
    try {
      const response = await apiClient.get(STATUS_URL);
      const normalized = normalizeStatus(response.data || {});
      setStatus(normalized);
      return normalized;
    } catch (error) {
      console.error("Error fetching status-check: ", error);
      const fallback = { ...status, enabled: status.enabled || false };
      setStatus(fallback);
      return fallback;
    }
  }, [setStatus, status]);

  const handleRouting = useCallback(
    (currentStatus) => {
      const isMaintenance = currentStatus.enabled;
      const isOnMaintenancePage = location.pathname === "/maintenance";

      if (isMaintenance) {
        if (!isOnMaintenancePage) {
          setIntendedPath(
            (prev) => prev || `${location.pathname}${location.search}`,
          );
          navigate("/maintenance", { replace: true });
        }
      } else if (isOnMaintenancePage) {
        navigate(intendedPath || "/", { replace: true });
        setIntendedPath(null);
      }
    },
    [intendedPath, location.pathname, location.search, navigate, setIntendedPath],
  );

  const refreshStatus = useCallback(async () => {
    const latestStatus = await checkStatus();
    handleRouting(latestStatus);
    return latestStatus;
  }, [checkStatus, handleRouting]);

  useEffect(() => {
    if (!enableAuto) return;
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableAuto]);

  useEffect(() => {
    if (!enableAuto) return;
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableAuto, location.pathname, location.search]);

  useEffect(() => {
    if (!enableAuto) return undefined;

    if (status.enabled) {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          refreshStatus();
        }, REFRESH_INTERVAL);
      }
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enableAuto, refreshStatus, status.enabled]);

  return { status, refreshStatus };
};

export default useMaintenanceStatus;
